import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { SubmitTicketCategoryConfig } from "../../../store/slices/ticketingPortalSlice";
import { useTheme } from "../../../theme";

type CloseTicketModalProps = {
  visible: boolean;
  onClose: () => void;
  ticketId: string;
  ticketCategory?: string;
  ticketSubCategory?: string;
  categoryConfig: SubmitTicketCategoryConfig[];
  onSubmit: (
    catId: number,
    subCatId: number,
    rcaCategory: string,
    rcaNotes: string,
    resolution: string,
    resolvedBy: string
  ) => Promise<{ msg: string; type: "success" | "error" | "info" | "warning" }>;
};

type DropdownProps = {
  label: string;
  value: string;
  options: Array<{ label: string; value: string }>;
  onChange: (val: string) => void;
  openKey: string;
  currentOpenKey: string | null;
  setOpenKey: (key: string | null) => void;
  isDark?: boolean;
};

const CustomDropdown: React.FC<DropdownProps> = ({
  label,
  value,
  options,
  onChange,
  openKey,
  currentOpenKey,
  setOpenKey,
  isDark,
}) => {
  const isOpen = currentOpenKey === openKey;

  if (Platform.OS === "web") {
    return (
      <View style={dropdownStyles.wrapper}>
        <Text style={[dropdownStyles.label, isDark && { color: "#B0B0B0" }]}>{label}</Text>
        <select
          value={value}
          onChange={(e: any) => onChange(e.target.value)}
          style={{
            width: "100%",
            height: 38,
            fontSize: 13,
            fontWeight: "500",
            paddingLeft: 10,
            paddingRight: 28,
            borderRadius: 7,
            border: isDark ? "1px solid #404040" : "1px solid #D0D0D0",
            backgroundColor: isDark ? "#2B2B2B" : "#ffffff",
            color: isDark ? "#F2F2F2" : "#2D2D2D",
            cursor: "pointer",
            appearance: "auto",
            colorScheme: isDark ? "dark" : "light",
          } as any}
        >
          <option value="">-- Select {label} --</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </View>
    );
  }

  const selectedOpt = options.find((opt) => opt.value === value);

  return (
    <View style={[dropdownStyles.wrapper, { zIndex: isOpen ? 100 : 1 }]}>
      <Text style={[dropdownStyles.label, isDark && { color: "#B0B0B0" }]}>{label}</Text>
      <Pressable
        onPress={() => setOpenKey(isOpen ? null : openKey)}
        style={({ pressed }) => [
          dropdownStyles.trigger,
          isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" },
          isOpen && dropdownStyles.triggerOpen,
          pressed && dropdownStyles.pressed,
        ]}
      >
        <Text style={[dropdownStyles.triggerText, isDark && { color: "#F2F2F2" }, !selectedOpt && dropdownStyles.placeholderText]}>
          {selectedOpt ? selectedOpt.label : `-- Select ${label} --`}
        </Text>
        <MaterialIcons
          name={isOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
          size={18}
          color={isDark ? "#B0B0B0" : "#374151"}
        />
      </Pressable>
      {isOpen && (
        <View style={[dropdownStyles.menu, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]}>
          <ScrollView nestedScrollEnabled style={{ maxHeight: 160 }}>
            {options.map((opt) => (
              <Pressable
                key={opt.value}
                onPress={() => {
                  onChange(opt.value);
                  setOpenKey(null);
                }}
                style={({ pressed }) => [
                  dropdownStyles.menuItem,
                  isDark && { borderBottomColor: "#363636" },
                  opt.value === value && (isDark ? { backgroundColor: "#1a1a1a" } : dropdownStyles.menuItemActive),
                  pressed && dropdownStyles.pressed,
                ]}
              >
                <Text
                  style={[
                    dropdownStyles.menuItemText,
                    isDark && { color: "#F2F2F2" },
                    opt.value === value && dropdownStyles.menuItemTextActive,
                  ]}
                >
                  {opt.label}
                </Text>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
};

export const CloseTicketModal: React.FC<CloseTicketModalProps> = ({
  visible,
  onClose,
  ticketId,
  ticketCategory,
  ticketSubCategory,
  categoryConfig = [],
  onSubmit,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [selectedCatId, setSelectedCatId] = useState<string>("");
  const [selectedSubCatId, setSelectedSubCatId] = useState<string>("");
  const [rcaCategory, setRcaCategory] = useState<string>("");
  const [rcaNotes, setRcaNotes] = useState<string>("");
  const [resolution, setResolution] = useState<string>("");
  const [resolvedBy, setResolvedBy] = useState<string>("");
  const [noteError, setNoteError] = useState<string>("");
  const [resolutionError, setResolutionError] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>("");

  useEffect(() => {
    if (!visible) return;

    setStep(1);
    setIsSubmitting(false);
    setOpenDropdown(null);
    setNoteError("");
    setResolutionError("");
    setSubmitError("");

    const matchedCat = categoryConfig.find(
      (category) => category.name.toLowerCase() === (ticketCategory ?? "").toLowerCase()
    );

    if (matchedCat) {
      setSelectedCatId(String(matchedCat.id));
      const matchedSub = matchedCat.subCategories?.find(
        (subCategory) => subCategory.name.toLowerCase() === (ticketSubCategory ?? "").toLowerCase()
      );
      setSelectedSubCatId(matchedSub ? String(matchedSub.id) : "");
    } else {
      setSelectedCatId("");
      setSelectedSubCatId("");
    }

    setRcaCategory("");
    setRcaNotes("");
    setResolution("");
    setResolvedBy("");
  }, [visible, ticketCategory, ticketSubCategory, categoryConfig]);

  const categoryOptions = useMemo(
    () => categoryConfig.map((category) => ({ label: category.name, value: String(category.id) })),
    [categoryConfig]
  );

  const subCategoryOptions = useMemo(() => {
    if (!selectedCatId) return [];
    const category = categoryConfig.find((item) => String(item.id) === selectedCatId);
    if (!category?.subCategories) return [];
    return category.subCategories.map((subCategory) => ({
      label: subCategory.name,
      value: String(subCategory.id),
    }));
  }, [selectedCatId, categoryConfig]);

  const rcaCategoryOptions = [
    "Software Bug",
    "Hardware Bug",
    "Configurations",
    "Enhancement Request",
    "User Error",
    "Knowledge Gap",
    "Business Request",
    "Proactive Monitoring",
  ].map((value) => ({ label: value, value }));

  const handleSubmit = async () => {
    if (!rcaNotes.trim()) {
      setNoteError("RCA Notes must not be empty.");
      return;
    }

    if (!resolution.trim()) {
      setResolutionError("Resolution must not be empty.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const result = await onSubmit(
        Number(selectedCatId),
        Number(selectedSubCatId),
        rcaCategory,
        rcaNotes.trim(),
        resolution.trim(),
        resolvedBy
      );

      if (result.type === "success") {
        onClose();
      } else {
        setSubmitError(result.msg);
      }
    } catch (error: any) {
      setSubmitError(error?.message || "An unexpected error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={modalStyles.overlay}>
        <View style={[modalStyles.card, isDark && { backgroundColor: "#202020" }]}>
          <View style={[modalStyles.header, isDark && { borderBottomColor: "#363636" }]}>
            <Text style={modalStyles.title}>Close Ticket</Text>
            <Pressable onPress={onClose} style={[modalStyles.closeBtn, isDark && { backgroundColor: "#2B2B2B" }]} disabled={isSubmitting}>
              <MaterialIcons name="close" size={20} color={isDark ? "#B0B0B0" : "#3A3A3A"} />
            </Pressable>
          </View>

          <ScrollView style={modalStyles.body} keyboardShouldPersistTaps="handled">
            {step === 1 && (
              <View style={modalStyles.stepContainer}>
                <Text style={[modalStyles.paragraph, isDark && { color: "#F2F2F2" }]}>Mark Ticket {ticketId} as Closed?</Text>
                <Text style={[modalStyles.subText, isDark && { color: "#B0B0B0" }]}>The submitter will be notified.</Text>
              </View>
            )}

            {step === 2 && (
              <View style={modalStyles.stepContainer}>
                <Text style={[modalStyles.paragraph, isDark && { color: "#F2F2F2" }]}>Confirm Category and Subcategory are accurate</Text>
                <CustomDropdown
                  label="Category"
                  value={selectedCatId}
                  options={categoryOptions}
                  onChange={(value) => {
                    setSelectedCatId(value);
                    setSelectedSubCatId("");
                  }}
                  openKey="category"
                  currentOpenKey={openDropdown}
                  setOpenKey={setOpenDropdown}
                  isDark={isDark}
                />
                {!!selectedCatId && subCategoryOptions.length > 0 && (
                  <CustomDropdown
                    label="Subcategory"
                    value={selectedSubCatId}
                    options={subCategoryOptions}
                    onChange={(value) => setSelectedSubCatId(value)}
                    openKey="subcategory"
                    currentOpenKey={openDropdown}
                    setOpenKey={setOpenDropdown}
                    isDark={isDark}
                  />
                )}
              </View>
            )}

            {step === 3 && (
              <View style={modalStyles.stepContainer}>
                <Text style={[modalStyles.paragraph, isDark && { color: "#F2F2F2" }]}>RCA Required to Close Ticket, Please Enter Below</Text>
                <CustomDropdown
                  label="RCA Category"
                  value={rcaCategory}
                  options={rcaCategoryOptions}
                  onChange={(value) => setRcaCategory(value)}
                  openKey="rcaCategory"
                  currentOpenKey={openDropdown}
                  setOpenKey={setOpenDropdown}
                  isDark={isDark}
                />

                <View style={{ marginTop: 12 }}>
                  <Text style={[modalStyles.inputLabel, isDark && { color: "#B0B0B0" }]}>RCA Notes (required)</Text>
                  <TextInput
                    style={[modalStyles.notesInput, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2" }, !!noteError && modalStyles.notesInputError]}
                    multiline
                    numberOfLines={4}
                    value={rcaNotes}
                    textAlignVertical="top"
                    onChangeText={(value) => {
                      setRcaNotes(value);
                      if (noteError) setNoteError("");
                      if (submitError) setSubmitError("");
                    }}
                  />
                  {!!noteError && <Text style={modalStyles.fieldErrorText}>{noteError}</Text>}
                </View>

                <View style={{ marginTop: 12 }}>
                  <Text style={[modalStyles.inputLabel, isDark && { color: "#B0B0B0" }]}>Resolution (required)</Text>
                  <TextInput
                    style={[modalStyles.notesInput, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2" }, !!resolutionError && modalStyles.notesInputError]}
                    multiline
                    numberOfLines={4}
                    value={resolution}
                    textAlignVertical="top"
                    onChangeText={(value) => {
                      setResolution(value);
                      if (resolutionError) setResolutionError("");
                      if (submitError) setSubmitError("");
                    }}
                  />
                  {!!resolutionError && <Text style={modalStyles.fieldErrorText}>{resolutionError}</Text>}
                </View>

                <View style={{ marginTop: 12 }}>
                  <Text style={[modalStyles.inputLabel, isDark && { color: "#B0B0B0" }]}>Resolved By:</Text>
                  <View style={modalStyles.radioGroup}>
                    {(["Me", "IT Teams", "Navi Team", "WES Team", "TMV Team", "End User"] as const).map((opt) => (
                      <TouchableOpacity
                        key={opt}
                        activeOpacity={0.7}
                        onPress={() => setResolvedBy(opt)}
                        style={modalStyles.radioRow}
                      >
                        <View style={[modalStyles.radioCircleOuter, isDark && { borderColor: "#6E6E6E" }]}>
                          {resolvedBy === opt && <View style={modalStyles.radioCircleInner} />}
                        </View>
                        <Text style={[modalStyles.radioLabel, isDark && { color: "#F2F2F2" }]}>{opt}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                {!!submitError && <Text style={[modalStyles.fieldErrorText, { marginTop: 10 }]}>{submitError}</Text>}
              </View>
            )}
          </ScrollView>

          <View style={[modalStyles.footer, isDark && { borderTopColor: "#363636" }]}>
            {step === 1 && (
              <>
                <Pressable style={[modalStyles.btnGhost, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]} onPress={onClose} disabled={isSubmitting}>
                  <Text style={[modalStyles.btnGhostText, isDark && { color: "#F2F2F2" }]}>Cancel</Text>
                </Pressable>
                <Pressable style={modalStyles.btnPrimary} onPress={() => setStep(2)}>
                  <Text style={modalStyles.btnPrimaryText}>Confirm</Text>
                </Pressable>
              </>
            )}

            {step === 2 && (
              <>
                <Pressable style={[modalStyles.btnGhost, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]} onPress={onClose} disabled={isSubmitting}>
                  <Text style={[modalStyles.btnGhostText, isDark && { color: "#F2F2F2" }]}>Cancel</Text>
                </Pressable>
                <Pressable style={modalStyles.btnPrimary} onPress={() => setStep(3)}>
                  <Text style={modalStyles.btnPrimaryText}>Confirm</Text>
                </Pressable>
              </>
            )}

            {step === 3 && (
              <>
                <Pressable style={[modalStyles.btnGhost, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]} onPress={onClose} disabled={isSubmitting}>
                  <Text style={[modalStyles.btnGhostText, isDark && { color: "#F2F2F2" }]}>Cancel</Text>
                </Pressable>
                <Pressable
                  style={[modalStyles.btnPrimary, isSubmitting && { opacity: 0.7 }]}
                  onPress={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <View style={modalStyles.submittingRow}>
                      <ActivityIndicator size="small" color="#ffffff" style={{ marginRight: 6 }} />
                      <Text style={modalStyles.btnPrimaryText}>Confirm</Text>
                    </View>
                  ) : (
                    <Text style={modalStyles.btnPrimaryText}>Confirm</Text>
                  )}
                </Pressable>
              </>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const modalStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  card: {
    backgroundColor: "#ffffff",
    width: "100%",
    maxWidth: 500,
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    display: "flex",
    flexDirection: "column",
    maxHeight: "90%",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  title: {
    fontSize: 17,
    fontWeight: "700",
    color: "#C12C01",
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
  },
  body: {
    flexGrow: 1,
  },
  stepContainer: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 8,
  },
  paragraph: {
    fontSize: 15,
    lineHeight: 21,
    color: "#111827",
    fontWeight: "600",
  },
  subText: {
    marginTop: 8,
    fontSize: 13,
    lineHeight: 19,
    color: "#4B5563",
  },
  inputLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: "#374151",
    marginBottom: 8,
  },
  notesInput: {
    minHeight: 96,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: "#111827",
    fontSize: 14,
    backgroundColor: "#ffffff",
  },
  notesInputError: {
    borderColor: "#C12C01",
  },
  fieldErrorText: {
    marginTop: 6,
    color: "#C12C01",
    fontSize: 12,
  },
  radioGroup: {
    gap: 10,
  },
  radioRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  radioCircleOuter: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 1.5,
    borderColor: "#6B7280",
    alignItems: "center",
    justifyContent: "center",
  },
  radioCircleInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#C12C01",
  },
  radioLabel: {
    fontSize: 14,
    color: "#111827",
    fontWeight: "500",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  btnGhost: {
    minWidth: 92,
    paddingHorizontal: 16,
    height: 40,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
  },
  btnGhostText: {
    color: "#374151",
    fontSize: 14,
    fontWeight: "600",
  },
  btnPrimary: {
    minWidth: 92,
    paddingHorizontal: 16,
    height: 40,
    borderRadius: 8,
    backgroundColor: "#C12C01",
    alignItems: "center",
    justifyContent: "center",
  },
  btnPrimaryText: {
    color: "#ffffff",
    fontSize: 14,
    fontWeight: "700",
  },
  submittingRow: {
    flexDirection: "row",
    alignItems: "center",
  },
});

const dropdownStyles = StyleSheet.create({
  wrapper: {
    marginTop: 12,
  },
  label: {
    fontSize: 13,
    fontWeight: "600",
    color: "#374151",
    marginBottom: 6,
  },
  trigger: {
    minHeight: 40,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  triggerOpen: {
    borderColor: "#C12C01",
  },
  triggerText: {
    flex: 1,
    fontSize: 14,
    color: "#111827",
    paddingRight: 10,
  },
  placeholderText: {
    color: "#9CA3AF",
  },
  menu: {
    marginTop: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    overflow: "hidden",
  },
  menuItem: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  menuItemActive: {
    backgroundColor: "#FFF1EA",
  },
  menuItemText: {
    fontSize: 14,
    color: "#111827",
  },
  menuItemTextActive: {
    color: "#C12C01",
    fontWeight: "600",
  },
  pressed: {
    opacity: 0.75,
  },
  errorText: {
    marginTop: 6,
    color: "#C12C01",
    fontSize: 12,
  },
});